'use client';
import { useEffect, useMemo, useState } from 'react';
import useServiceProviders from 'Common/hooks/useServiceProviders';
import getUserWithLocations, { GetUserWithLocations } from 'Common/functions/getUserWithLocations';
import { QueryServiceProviders } from 'Common/functions/guestQueryServiceProviders';
import { AppCookie, setCookie } from 'Common/functions/Cookie';
import { sortLocationsByStreetAddress } from 'Common/functions/Location';
import BrowseServiceProvidersPageContent from '../BrowseServiceProvidersPageContent';
import { useApolloClient } from '@apollo/client';
import { formatServiceTypeSingular } from '@bookpeazy/common-js';
import NoSSR from 'Common/hoc/NoSSR';
import PromptCreateHomeLocation from '../PromptCreateHomeLocation';
import signInAnonymously from 'Common/functions/signInAnonymously';
import { getAuth } from 'firebase/auth';
import { getInitialSelectedLocationId } from 'Common/functions/LocationCookie';

export type BrowseServiceProvidersPageProps = {
  serviceProviders: QueryServiceProviders;
  defaultSelectedLocationId: string | null;
  locations: GetUserWithLocations['locations']
  isTemporaryUser: boolean;
  serviceType: string;
}

const BrowseServiceProvidersPage: React.FC<BrowseServiceProvidersPageProps> = ({
  serviceProviders: ssrServiceProviders,
  defaultSelectedLocationId,
  locations: pLocations,
  isTemporaryUser,
  serviceType,
}) => {
  const [locations, setLocations] = useState(sortLocationsByStreetAddress(pLocations));

  const initialSelectedLocationId =
    getInitialSelectedLocationId(locations, defaultSelectedLocationId);
  const [selectedLocationId, setSelectedLocationId] =
    useState<string | null>(initialSelectedLocationId);
  const selectedLocation = useMemo(
    () => locations.length > 0 ? locations.find(l => l.id === selectedLocationId) || locations[0] : undefined,
    [locations, selectedLocationId],
  );

  const lat = selectedLocation ? selectedLocation.latitude : null;
  const lng = selectedLocation ? selectedLocation.longitude : null;
  const client = useApolloClient();

  const [isPromptCreateLocationModelOpen, setIsPromptCreateLocationModelOpen] =
    useState(locations.length === 0);

  const refetchLocations = async () => {
    const res = await getUserWithLocations(client.query, undefined);
    if (res?.locations) {
      const newLocs = sortLocationsByStreetAddress(res.locations);
      setLocations(newLocs);
      return newLocs;
    }
  };

  const [
    clientSideServiceProviders,
    unused,
    clientSideServiceProvidersIsLoading,
  ] = useServiceProviders((lat && lng) ? {
    lat,
    lng,
    page: 1,
    pageSize: 50,
    serviceTypes: [serviceType],
    isApproved: true,
  } : undefined);
  const serviceProviders = useMemo(
    () => clientSideServiceProviders || ssrServiceProviders,
    [clientSideServiceProviders, ssrServiceProviders],
  );

  useEffect(() => {
    if (!defaultSelectedLocationId && locations.length > 0) {
      setCookie(AppCookie.CustomerSelectedLocationId, locations[0].id, 1000);
    }
  }, []);

  return (
    <>
      <BrowseServiceProvidersPageContent
        serviceProviders={serviceProviders}
        locations={locations}
        isTemporaryUser={isTemporaryUser}
        serviceType={serviceType}
        setSelectedLocationId={s => setSelectedLocationId(s)}
        refetchLocations={async () => {
          await refetchLocations();
        }}
        selectedLocationId={selectedLocation?.id}
        isLoading={clientSideServiceProvidersIsLoading}
        breadcrumbTitle={`${formatServiceTypeSingular(serviceType)} Providers Near ${selectedLocation?.streetAddress || 'Me'}`}
      />
      <NoSSR>
        <PromptCreateHomeLocation
          isOpen={isPromptCreateLocationModelOpen}
          onClose={() => setIsPromptCreateLocationModelOpen(false)}
          onFlowComplete={(loc) => {
            setSelectedLocationId(loc.id);
            setIsPromptCreateLocationModelOpen(false);
            refetchLocations();
          }}
          onBeforeSubmit={async next => {
            if (isTemporaryUser) {
              await signInAnonymously(getAuth());
            }

            next();
          }}
        />
      </NoSSR>
    </>
  );
};

export default BrowseServiceProvidersPage;

